import type { ShopperProducts } from '@/scapi';
import { getCategoryDisplayName } from '@/lib/category-display-name';
import { createCategoryUrl } from '@/lib/category-url';

/**
 * Shared category taxonomy helpers (SEO sibling/child selection, breadcrumb tree URLs).
 * Category navigation targets always use each category's own `c_url` via `createCategoryUrl`.
 */

export type CategoryTreeUrlSegment = {
    id?: string;
    name?: string;
    url: string;
};

const STOREFRONT_CATALOG_ROOT_SUFFIX = '-storefront-catalog';

/**
 * Synthetic catalog root (`root`) and site catalog assignment roots (e.g. `EAT1-storefront-catalog`)
 * are omitted from breadcrumb trails — they are not shopper-facing navigation nodes.
 */
export function isBreadcrumbExcludedCategoryId(categoryId: string | undefined | null): boolean {
    const id = categoryId?.trim();
    if (!id || id === 'root') {
        return true;
    }

    return id.endsWith(STOREFRONT_CATALOG_ROOT_SUFFIX);
}

/**
 * Treat expanded children as authoritative when present, otherwise fall back to
 * SCAPI's online subcategory count.
 */
export function isLeafCategory(category: ShopperProducts.schemas['Category']): boolean {
    if (category.categories?.length) {
        return false;
    }

    return (category.onlineSubCategoriesCount ?? 0) === 0;
}

/**
 * Maps `parentCategoryTree` to linked segments, appending the primary category when
 * it is missing from the tree. Used by PLP and PDP breadcrumb builders.
 *
 * When the current category is already in the tree, prefer its full SCAPI payload for
 * the leaf label so `c_shortnameOverride` is used (tree nodes often omit custom attrs).
 */
export function mapCategoryTreeToUrls(category: ShopperProducts.schemas['Category']): CategoryTreeUrlSegment[] {
    const tree = (category.parentCategoryTree ?? []).filter((node) => !isBreadcrumbExcludedCategoryId(node.id));
    const segments = tree.map((node) => {
        const isCurrent = node.id != null && node.id === category.id;
        return {
            id: node.id,
            name: getCategoryDisplayName(isCurrent ? category : node),
            url: createCategoryUrl(node),
        };
    });

    const hasPrimaryInTree = tree.some((node) => node.id != null && node.id === category.id);
    if (!hasPrimaryInTree && category.id && !isBreadcrumbExcludedCategoryId(category.id)) {
        segments.push({
            id: category.id,
            name: getCategoryDisplayName(category),
            url: createCategoryUrl(category),
        });
    }

    return segments;
}
